import React, { useState } from "react";

type Variant = "primary" | "secondary" | "ghost";
type Size = "sm" | "md" | "lg";

// Heights mirror the handoff's hint-size usage (sm 38, md 44, lg 50).
const SIZES: Record<Size, { height: number; padding: string; font: number }> = {
  sm: { height: 38, padding: "0 16px", font: 13 },
  md: { height: 44, padding: "0 20px", font: 14 },
  lg: { height: 50, padding: "0 24px", font: 15 },
};

function base(variant: Variant, size: Size, disabled: boolean): React.CSSProperties {
  const s = SIZES[size];
  const common: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    height: s.height,
    padding: s.padding,
    fontFamily: "var(--font-display)",
    fontWeight: 600,
    fontSize: s.font,
    letterSpacing: "-0.01em",
    borderRadius: 10,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    transition: "background 120ms ease, border-color 120ms ease, transform 120ms ease",
    whiteSpace: "nowrap",
  };
  if (variant === "primary")
    return { ...common, background: "var(--violet-500)", color: "#fff", border: "none" };
  if (variant === "secondary")
    return {
      ...common,
      background: "#fff",
      color: "var(--violet-500)",
      border: "1px solid var(--violet-100)",
    };
  return {
    ...common,
    background: "transparent",
    color: "var(--ink-700)",
    border: "1px solid var(--border-strong)",
  };
}

function hover(variant: Variant): React.CSSProperties {
  if (variant === "primary") return { background: "var(--violet-600)" };
  if (variant === "secondary") return { background: "var(--violet-50)" };
  return { background: "var(--surface-sunken)", borderColor: "var(--violet-200)" };
}

export default function Button({
  variant = "primary",
  size = "md",
  onClick,
  disabled = false,
  type = "button",
  style,
  children,
}: {
  variant?: Variant;
  size?: Size;
  onClick?: () => void;
  disabled?: boolean;
  type?: "button" | "submit";
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onMouseDown={(e) => (e.currentTarget.style.transform = "scale(0.98)")}
      onMouseUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
      style={{
        ...base(variant, size, disabled),
        ...(hovered && !disabled ? hover(variant) : null),
        ...style,
      }}
    >
      {children}
    </button>
  );
}
